import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Infinity as InfinityIcon, Lock, RefreshCw, ShieldCheck, Timer, X } from 'lucide-react';
import { useStore } from '../store/useStore';
import { PLANS, type PaidPlan } from '../types';
import { lifetimeCountdown } from '../billing/lifetimeStats';
import { priceLabel } from '../billing/prices';
import { PRELAUNCH_NOTE, STORE_BILLING_LIVE, WAITLIST_URL } from '../billing/launch';
import { isIOS } from '../native/native';
import { publicUrl } from '../native/native';
import { storeName } from '../billing/storeLabels';

/** The trial paywall bottom sheet — 3 tiers, mock billing (no charges). */
export function PaywallSheet() {
  const open = useStore((s) => s.paywallOpen);
  const storePrices = useStore((s) => s.storePrices);
  const closePaywall = useStore((s) => s.closePaywall);
  const subscribe = useStore((s) => s.subscribe);
  const billingNotice = useStore((s) => s.billingNotice);
  const billingBusy = useStore((s) => s.billingBusy);
  const [plan, setPlan] = useState<PaidPlan>('annual');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteMsg, setInviteMsg] = useState<string | null>(null);
  const [redeeming, setRedeeming] = useState(false);
  const [lifetimeSold, setLifetimeSold] = useState<number | null>(null);
  const syncStatus = useStore((s) => s.syncStatus);
  const syncNotice = useStore((s) => s.syncNotice);
  const syncError = useStore((s) => s.syncError);
  const [inviteSigningIn, setInviteSigningIn] = useState(false);
  const signInForInvite = async () => {
    setInviteSigningIn(true);
    try {
      const { signInWithGoogle } = await import('../sync/firebaseSync');
      await signInWithGoogle();
    } finally {
      setInviteSigningIn(false);
    }
  };

  // Live launch-window scarcity: fetch the sold count when the sheet opens.
  useEffect(() => {
    if (!open) return;
    void import('../sync/firebaseSync').then(async (m) => {
      setLifetimeSold(await m.readLifetimeSold());
    });
  }, [open]);

  const countdown = lifetimeCountdown(lifetimeSold);
  // Sunset enforcement: at the cap the lifetime tier simply stops existing.
  const visiblePlans = PLANS.filter((p) => p.id !== 'lifetime' || !countdown.soldOut);

  // Never leave a hidden tier selected.
  useEffect(() => {
    if (countdown.soldOut && plan === 'lifetime') setPlan('annual');
  }, [countdown.soldOut, plan]);

  const tryRedeem = async () => {
    setRedeeming(true);
    setInviteMsg(null);
    try {
      const { redeemInvite } = await import('../alpha/alphaProgram');
      const result = await redeemInvite(inviteCode);
      if (result === 'ok') {
        setInviteCode('');
        setShowInvite(false);
        return;
      }
      setInviteMsg(
        {
          invalid: "That code isn't valid — check it and try again.",
          used: 'That code has already been claimed by another account.',
          'signed-out':
            'Sign in first (Settings → Cloud Sync — Google or any email) so your lifetime access is tied to your account.',
          offline: 'Cloud sync must be configured to redeem invite codes.',
          error: 'Something went wrong — check your connection and try again.',
        }[result],
      );
    } finally {
      setRedeeming(false);
    }
  };

  const selected = PLANS.find((p) => p.id === plan);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 dark:bg-slate-950/70 backdrop-blur-sm"
          onClick={closePaywall}
        >
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-h-[92vh] w-full max-w-md space-y-4 overflow-y-auto rounded-t-3xl border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-2xl"
          >
            <div className="mx-auto mb-1 h-1.5 w-12 rounded-full bg-slate-300 dark:bg-slate-700" />

            {/* Always-visible escape hatch — a tester got stranded in this
                sheet with the action buttons scrolled off-screen (2 Aug).
                Tapping the backdrop also closes, but a visible ✕ is the
                honest affordance. */}
            <button
              onClick={closePaywall}
              aria-label="Close"
              className="absolute right-4 top-4 rounded-lg p-1.5 text-slate-400 transition-colors hover:text-slate-700 dark:hover:text-slate-200"
            >
              <X size={18} />
            </button>

            <div className="space-y-2 text-center">
              <span className="inline-flex rounded-2xl border border-brand-300 dark:border-brand-400/20 bg-brand-500/10 p-2.5 text-brand-600 dark:text-brand-400">
                <ShieldCheck size={26} />
              </span>
              <h3 className="text-xl font-black text-slate-900 dark:text-white">Unlock your full toolkit</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Every plan includes everything — just pick how you'd like to pay.
              </p>
            </div>

            <div className="space-y-1 rounded-2xl border border-brand-300 dark:border-brand-500/30 bg-brand-50 dark:bg-brand-900/20 p-3.5 text-center">
              <p className="flex items-center justify-center gap-1.5 text-xs font-extrabold text-slate-900 dark:text-white">
                <InfinityIcon size={15} className="text-brand-600 dark:text-brand-400" />
                100% UNLIMITED ACCESS
              </p>
              <p className="text-[11px] leading-normal text-brand-700 dark:text-brand-200">
                Every paid tier includes unlimited AI scans, unlimited smart
                roadmaps, and full mastery analytics — no hidden limits.
              </p>
            </div>

            <div className="space-y-2">
              {visiblePlans.map((p) => {
                const active = plan === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setPlan(p.id)}
                    className={`flex w-full items-center justify-between rounded-xl p-3.5 text-left transition-all ${
                      active
                        ? 'border-2 border-brand-500 bg-brand-500/10'
                        : 'border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 hover:border-slate-300 dark:hover:border-slate-700'
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <span
                        className={`flex h-4 w-4 items-center justify-center rounded-full border-2 p-0.5 ${
                          active ? 'border-brand-500' : 'border-slate-300 dark:border-slate-700'
                        }`}
                      >
                        <span
                          className={`h-full w-full rounded-full ${active ? 'bg-brand-500' : 'bg-transparent'}`}
                        />
                      </span>
                      <span>
                        <span className="block text-xs font-bold text-slate-900 dark:text-white">{p.title}</span>
                        <span
                          className={`text-[11px] font-medium ${active ? 'text-brand-600 dark:text-brand-400' : 'text-slate-500'}`}
                        >
                          {p.subtitle}
                        </span>
                        {p.id === 'lifetime' && countdown.showCountdown && (
                          <span className="mt-1 inline-block rounded-full border border-red-200 dark:border-red-500/30 bg-red-500/10 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-red-600 dark:text-red-400">
                            Only {countdown.remaining} of 500 left
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="text-right">
                      <span className="block text-sm font-black text-slate-900 dark:text-white">{priceLabel(storePrices[p.id], p.price)}</span>
                      {p.trial && (
                        <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald-600 dark:text-emerald-400">
                          7-Day Trial
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex justify-around rounded-xl border border-slate-200 dark:border-slate-800/80 bg-slate-50 dark:bg-slate-950/50 p-2.5 text-center text-[11px] text-slate-500 dark:text-slate-400">
              <span className="flex flex-col items-center gap-1">
                <Lock size={15} className="text-brand-600 dark:text-brand-400" />
                Secure Checkout
              </span>
              <span className="flex flex-col items-center gap-1">
                <RefreshCw size={15} className="text-brand-600 dark:text-brand-400" />
                Cancel anytime
              </span>
              <span className="flex flex-col items-center gap-1">
                <Timer size={15} className="text-brand-600 dark:text-brand-400" />
                {selected?.trial ? '7-day free trial' : 'Instant access'}
              </span>
            </div>

            {selected?.trial && (
              <p className="text-center text-xs leading-relaxed text-slate-500">
                Try premium risk-free. Your 7-day free trial starts now — a
                full week to run a real weekly cycle. Cancel before it ends in
                your {storeName()} subscription settings and you won't be
                charged.
              </p>
            )}

            {/* iOS binary: the invite-code field is hidden — an external
                unlock mechanism is an App Review 3.1.1 risk. Existing
                lifetime holders keep access through their signed-in
                account (grant doc); nothing is lost (APPLE_LAUNCH_PLAN §1.3).
                What iOS DOES say (7 Oct 2026): a signpost to sign in with the
                same account. It points at access the person already holds —
                no code, no web page, no outside purchase — so it stays inside
                3.1.1 / 3.1.3(b). Same sentence as Settings → Cloud Sync. */}
            {isIOS() && syncStatus !== 'syncing' && (
              <p className="text-center text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                Already have lifetime access? Sign in with the same account you used before — your access follows your account.
              </p>
            )}
            {!isIOS() && (
            <div className="text-center">
              {!showInvite ? (
                <button
                  onClick={() => setShowInvite(true)}
                  className="text-[11px] font-semibold text-slate-500 underline-offset-2 hover:underline"
                >
                  Alpha tester? Enter your invite code
                </button>
              ) : syncStatus !== 'syncing' ? (
                /* Not signed in with a real account yet: sign-in happens
                   RIGHT HERE — the redeem flow must never dead-end into
                   "go to Settings first" (Chad, 2 Aug). On native this
                   opens the native Google sheet; the code input appears
                   the moment sign-in lands. */
                <div className="space-y-2">
                  <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
                    First, sign in — your lifetime access ties to your
                    account, so it follows you to every device.
                  </p>
                  <button
                    onClick={() => void signInForInvite()}
                    disabled={inviteSigningIn}
                    className="rounded-lg bg-brand-600 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-brand-500 disabled:opacity-60"
                  >
                    {inviteSigningIn ? 'Opening Google…' : 'Sign in with Google to continue'}
                  </button>
                  {syncNotice && (
                    <p className="break-words rounded-lg border border-brand-100 dark:border-brand-500/20 bg-brand-50 dark:bg-brand-900/20 p-2 text-[11px] leading-relaxed text-brand-700 dark:text-brand-100">
                      {syncNotice}
                    </p>
                  )}
                  {!syncNotice && !inviteSigningIn && syncStatus === 'error' && syncError && (
                    <p className="break-words rounded-lg bg-red-50 dark:bg-red-950/20 p-2 font-mono text-[10px] leading-relaxed text-red-700 dark:text-red-300">
                      {syncError}
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-1.5">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={inviteCode}
                      onChange={(e) => {
                        setInviteCode(e.target.value);
                        setInviteMsg(null);
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && void tryRedeem()}
                      autoComplete="off"
                      name="zonein-invite-code"
                      placeholder="ZONEIN-ALPHA-…"
                      className="min-w-0 flex-1 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 px-3 py-2 text-center font-mono text-xs uppercase text-slate-900 dark:text-white placeholder-slate-400 focus:border-brand-500 focus:outline-none"
                    />
                    <button
                      onClick={() => void tryRedeem()}
                      disabled={redeeming}
                      className="rounded-lg bg-brand-600 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-brand-500 disabled:opacity-60"
                    >
                      {redeeming ? 'Checking…' : 'Redeem'}
                    </button>
                  </div>
                  {inviteMsg && (
                    <p className="text-[11px] text-red-600 dark:text-red-400">{inviteMsg}</p>
                  )}
                </div>
              )}
            </div>
            )}

            {!STORE_BILLING_LIVE && (
              <p className="rounded-xl bg-brand-600/10 px-3 py-2 text-center text-[11px] font-semibold text-brand-700 dark:text-brand-300">
                {PRELAUNCH_NOTE}
              </p>
            )}
            {/* Store outcome, verbatim (CLAUDE.md §2) — a failed purchase must
                never look like "nothing happened". */}
            {billingNotice && (
              <p
                role="status"
                className="break-words rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-[11px] leading-relaxed text-amber-800 dark:text-amber-200"
              >
                {billingNotice}
              </p>
            )}
            <div className="flex gap-3">
              <button
                onClick={closePaywall}
                className="w-1/3 rounded-xl border border-slate-200 dark:border-slate-800 py-3 text-sm font-medium text-slate-500 dark:text-slate-400 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800"
              >
                Not Now
              </button>
              {STORE_BILLING_LIVE ? (
                <button
                  onClick={() => void subscribe(plan)}
                  disabled={billingBusy}
                  className="w-2/3 rounded-xl bg-brand-600 py-3 text-sm font-bold text-white shadow-lg shadow-brand-600/20 transition-all hover:bg-brand-500 active:scale-[0.98] disabled:opacity-60"
                >
                  {billingBusy
                    ? `Opening ${storeName()}…`
                    : selected?.trial
                      ? 'Start My Free Trial'
                      : 'Activate Plan'}
                </button>
              ) : (
                <a
                  href={publicUrl(WAITLIST_URL)}
                  target="_blank"
                  rel="noopener"
                  className="flex w-2/3 items-center justify-center rounded-xl bg-brand-600 py-3 text-sm font-bold text-white shadow-lg shadow-brand-600/20 transition-all hover:bg-brand-500 active:scale-[0.98]"
                >
                  Join the launch waitlist
                </a>
              )}
            </div>
            {/* App Review 3.1.2: the purchase screen itself must state how
                subscriptions renew and link to the Terms of Use and the
                Privacy Policy (12 Oct 2026 — the paywall had neither). */}
            {STORE_BILLING_LIVE && (
              <p className="text-center text-[10px] leading-relaxed text-slate-500 dark:text-slate-400">
                Subscriptions renew automatically unless cancelled at least 24
                hours before the end of the current period; manage them in
                your {storeName()} account settings. Lifetime is a one-time
                purchase.{' '}
                <a href={publicUrl('/terms.html')} target="_blank" rel="noopener" className="font-semibold underline">
                  Terms of Use
                </a>{' '}
                ·{' '}
                <a href={publicUrl('/privacy.html')} target="_blank" rel="noopener" className="font-semibold underline">
                  Privacy Policy
                </a>
              </p>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
